<template>
  <div class="layout-builder p-3" v-if="grid">
    <el-empty :description="$i18n.t('empty page')" v-if="!items || !items.length"></el-empty>
    <layout-row
      v-for="(row, gridId) in gridLayout"
      class="row"
      :key="gridId"
      :grid="grid"
      :row="row"
      :url="url"
      :params="params"
    />
    <template v-if="!isSubBlock">
      <popup v-for="(popup, i) in popups" :level="i" :key="popupIndex(i)" />
    </template>
  </div>
</template>

<script>
import Popup from '../../Popup'
import LayoutRow from './LayoutRow'
import EventBus from '../../../utils/event-bus'
import core from '../../../utils/mixin-core'
import notify from '../../../utils/mixin-notify'
import { mapGetters } from 'vuex'
import { EMPTY } from '../lib/helpers'
import { countBy, get, some } from 'lodash'
import { helpyTypes } from '../../helpy-designer/helpyTypes'
import { SIMPLEPAY_FINAL_STATUSES } from '@/consts'

  /**
   * @class LayoutBuilder
   *
   * @description This component renders the Layout with the Panels
   */
  export default {
    name: 'LayoutBuilder',
    mixins: [notify, core],
    components: {
      Popup,
      LayoutRow,
    },
    props: {
      url: {
        type: String,
        required: true,
      },
      params: {
        type: Object,
        default: () => ({}),
      },
      isSubBlock: {
        type: Boolean,
        required: false,
      },
    },
    data() {
      return {
        grid: null,
        gridLayout: [],
      }
    },
    computed: {
      ...mapGetters('popup', ['popups']),
      layout() {
        return this.items.map((item) => item.position)
      },
      items() {
        return get(this.grid, 'gridItems', [])
      },
    },
    methods: {
      handleSimplePayResponse() {
        const { orderRef, transactionId, transactionStatus } = CORE.simplePayResponse

        if (!SIMPLEPAY_FINAL_STATUSES.includes(transactionStatus)) {
          const fetchInternal = setInterval(() => {
            this.$client
                .execute({
                  operationId: 'PaymentTransaction_getStatus',
                  parameters: { orderRef, transactionId },
                })
                .then((response) => {
                  const responseStatus = get(response, 'obj.transactionStatus')
                  this.$set(CORE.simplePayResponse, 'transactionStatus', responseStatus)
                  if (SIMPLEPAY_FINAL_STATUSES.includes(responseStatus)) clearInterval(fetchInternal)
                })
          }, 2500)

          /* Timeout after 5 min */
          setTimeout(() => {
            clearInterval(fetchInternal)
          }, 300000)
        }
      },
      async loadSqlParams() {
        const params = { ...this.$CORE.params, ...this.params }

        let routedUrl
        if (this.isSubBlock) {
          routedUrl = this.url
          params.route = this.url
        }

        try {
          const response = await this.$client.execute({
            operationId: 'ExternalParams_getSqlParams',
            parameters: {
              routedUrl,
              dotoParams: JSON.stringify(params),
            },
          })

          const sqlParams = get(response, 'obj', {})

          if (window.CORE.sqlParams) {
            Object.assign(window.CORE.sqlParams, sqlParams)
          } else {
            window.CORE.sqlParams = sqlParams
          }
        } catch (error) {
          const noty = {
            title: 'sqlparams_error',
            text: 'sqlparams_refresh_error',
            type: 'info',
          }
          this.showNotification(noty)
        }
      },
      handleFreshLayout(layout) {
        const hasDuplicateValues = some(
          countBy(layout.gridItems, (item) => item.position.i),
          (val) => val !== 1
        )
        layout.gridItems.forEach((item, index) => {
          item.columnId = 'empty'
          item.height = 140
          item.tabId = 'tab-1'
          item.tabTitle = 'Default'

          if (hasDuplicateValues) {
            item.i = index
            item.position.i = index
          }
        })
        layout.tabs = {
          empty: [
            {
              name: 'tab-1',
              position: 'empty',
              title: 'Default',
            },
          ],
        }
        layout.tabProperties = {
          stretch: true,
          tabPosition: 'top',
          type: 'border-card',
        }
        return layout
      },
      async generateLayout() {
        const defaultGridLayout = [[{ columnId: EMPTY, width: 12 }]]

        if (this.url === '/') {
          this.url = get(this.$CORE, 'landingPage')
        }

        try {
          const layoutManager = await this.$client.apis['LayoutManager']['LayoutManager_get']({
            filter: JSON.stringify({
              where: {
                url: this.url,
              },
            }),
            params: JSON.stringify({
              language: window.CORE.language,
            }),
          })
          if (!layoutManager.obj) {
            this.gridLayout = defaultGridLayout
            return
          }
          this.grid = layoutManager.obj
          this.grid.gridItems = this.grid.gridItems.filter(
            (panel) => panel.params.system === window.CORE.subSystem.id || !panel.params.system
          )
          if (this.grid.gridLayoutName) {
            try {
              const parameters = {
                filter: JSON.stringify({
                  where: {
                    name: this.grid.gridLayoutName,
                  },
                }),
              }
              const gridLayout = await this.$client.execute({
                operationId: 'GridLayout_findOne',
                parameters,
              })
              this.gridLayout = get(gridLayout, 'obj.setup', defaultGridLayout)
            } catch (error) {
              this.gridLayout = defaultGridLayout
            }
          } else {
            this.gridLayout = defaultGridLayout
          }

          if (!this.grid.hasOwnProperty('tabs')) {
            this.handleFreshLayout(this.grid)
          }
        } catch (error) {
          // @todo log
          console.error('Layout not found')
        }
      },
      popupIndex(i) {
        return 'popup-index-' + i
      },
      async registerEventListener() {
        this.CORE.socket.addEventListener('refresh-layout', async (data) => {
          const dataUserId = get(data, 'userId', null)
          const dataUrl = get(data, 'url', null)

          const idSUser = get(this.CORE, 'params.ID_S_USER', null)
          const requestRoute = get(this.CORE, 'requestRoute', null)

          const statement =
            (dataUserId === idSUser && dataUrl === requestRoute) ||
            (dataUserId === idSUser && !dataUrl) ||
            (!dataUserId && dataUrl === requestRoute) ||
            (!dataUrl && !dataUserId)

          if (statement) EventBus.$emit('refresh-layout')
        })
      },
      async loadHelpy() {
        const url = this.$route.path.replace(/^\//, '') // @regex string elejen /-t kitorol
        const parameters = { url, type: helpyTypes[1].value }

        if (!get(window, 'CORE.params.ID_S_USER')) return

        const helpy = this.$CORE.helpy || []

        const helpies = helpy.reduce(function (acc, curr) {
          acc[curr.selector] = acc[curr.selector] || []
          acc[curr.selector].push(curr)
          return acc
        }, {})

        this.$store.dispatch('loadHelpy', helpies)
      },
    },
    async mounted() {
      EventBus.$on('refresh-layout', async () => {
        if (this.$route.path.includes('/form-designer/')) return

        await this.loadSqlParams()
        await this.generateLayout()
        EventBus.$emit('layout-updated')
        await this.loadHelpy()
      })

      if (this.isSubBlock) await this.loadSqlParams()

      if (get(this.CORE, 'socket')) {
        this.registerEventListener()
      }

      if (CORE.simplePayResponse) this.handleSimplePayResponse()
    },
    beforeDestroy() {
      EventBus.$off('refresh-layout')
      if (get(this.CORE, 'socket')) {
        this.CORE.socket.removeEventListener('refresh-layout')
      }
    },
    async created() {
      await this.generateLayout()
      await this.loadHelpy()
    },
  }
</script>
<style scoped>
  .layout-builder {
    position: relative;
    top: 4em;
  }
</style>
